import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Pencil, Trash2, AlertCircle, X, Search, Receipt, IndianRupee, Briefcase,
} from 'lucide-react';
import { adminApi, formatApiError } from '../../utils/adminApi';
import {
  fmtINR, fmtDate, todayISO, EXPENSE_CATEGORIES, OVERHEAD_CATEGORIES,
} from '../../utils/billingFormat';

const SUBTABS = [
  { id: 'dashboard', label: 'Dashboard', icon: IndianRupee },
  { id: 'orders', label: 'Order Expenses', icon: Receipt },
  { id: 'overhead', label: 'Overhead Costs', icon: Briefcase },
];

const CAT_LABEL = Object.fromEntries(EXPENSE_CATEGORIES.map((c) => [c.id, c.label]));
const OVH_LABEL = Object.fromEntries(OVERHEAD_CATEGORIES.map((c) => [c.id, c.label]));

const ExpenseTab = ({ onToast }) => {
  const [sub, setSub] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [overheads, setOverheads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [editingOrder, setEditingOrder] = useState(null);
  const [editingOverhead, setEditingOverhead] = useState(null);

  const refresh = async () => {
    setLoading(true); setError('');
    try {
      const [s, o, oh] = await Promise.all([
        adminApi.expenseStats(),
        adminApi.listExpenseOrders(),
        adminApi.listOverheads(),
      ]);
      setStats(s); setOrders(o); setOverheads(oh);
    } catch (err) { setError(formatApiError(err, 'Could not load expenses.')); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const handleDeleteOrder = async (o) => {
    if (!o.id) return;
    if (!window.confirm('Remove this expense record?')) return;
    try {
      await adminApi.deleteExpenseOrder(o.id);
      onToast?.('Expense entry removed');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  const handleDeleteOverhead = async (o) => {
    if (!window.confirm(`Delete overhead "${o.desc}"?`)) return;
    try {
      await adminApi.deleteOverhead(o.id);
      onToast?.('Overhead entry deleted');
      refresh();
    } catch (err) { setError(formatApiError(err)); }
  };

  return (
    <div data-testid="expense-tab">
      <div className="billing-subtabs">
        {SUBTABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              className={`billing-subtab ${sub === t.id ? 'active' : ''}`}
              onClick={() => setSub(t.id)}
              data-testid={`expense-subtab-${t.id}`}
            >
              <Icon size={12} style={{ marginRight: 6, verticalAlign: '-1px' }} />
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="billing-sync-banner" data-testid="expense-sync-banner">
        <AlertCircle size={14} /> Order revenues sync automatically from <strong style={{ marginLeft: 4 }}>Billing → Invoices</strong>. Add per-order expenses against any invoice to track its profit.
      </div>

      {error && <div className="admin-error"><AlertCircle size={14} /> {error}</div>}
      {loading && <p className="body-regular">Loading…</p>}

      {!loading && sub === 'dashboard' && <ExpenseDashboard stats={stats} />}

      {!loading && sub === 'orders' && (
        <OrdersView
          orders={orders}
          onEdit={setEditingOrder}
          onDelete={handleDeleteOrder}
        />
      )}

      {!loading && sub === 'overhead' && (
        <OverheadView
          overheads={overheads}
          onCreate={() => setEditingOverhead({})}
          onEdit={setEditingOverhead}
          onDelete={handleDeleteOverhead}
        />
      )}

      {editingOrder && (
        <OrderExpenseModal
          initial={editingOrder}
          onClose={() => setEditingOrder(null)}
          onSaved={(_, isEdit) => {
            setEditingOrder(null);
            onToast?.(isEdit ? 'Expense updated' : 'Expense saved');
            refresh();
          }}
        />
      )}

      {editingOverhead && (
        <OverheadModal
          initial={editingOverhead}
          onClose={() => setEditingOverhead(null)}
          onSaved={(_, isEdit) => {
            setEditingOverhead(null);
            onToast?.(isEdit ? 'Overhead updated' : 'Overhead added');
            refresh();
          }}
        />
      )}
    </div>
  );
};

/* ---------------- Dashboard ---------------- */
const ExpenseDashboard = ({ stats }) => {
  const cats = stats?.categories || {};
  return (
    <>
      <div className="billing-stats-grid">
        <StatCard label="Total Revenue" value={fmtINR(stats?.total_revenue)} note="From all invoices" tone="success" />
        <StatCard label="Total Expenses" value={fmtINR(stats?.total_expenses)} note={`Orders + overheads`} tone="warn" />
        <StatCard label="Net Profit" value={fmtINR(stats?.net_profit)} note="Revenue − all expenses" tone={Number(stats?.net_profit || 0) >= 0 ? 'success' : 'warn'} />
        <StatCard label="Avg Margin" value={`${stats?.avg_margin || 0}%`} note="Net profit / revenue" />
      </div>

      <h3 className="billing-section-title" style={{ marginBottom: 14 }}>Cost Breakdown by Category</h3>
      <div className="expense-cat-grid">
        {EXPENSE_CATEGORIES.map((c) => (
          <div className="expense-cat-card" key={c.id}>
            <p className="lbl">{c.label}</p>
            <p className="val">{fmtINR(cats[c.id] || 0)}</p>
          </div>
        ))}
      </div>

      <div className="billing-section">
        <div className="billing-section-header">
          <h3 className="billing-section-title">Recent Orders — Profit &amp; Loss</h3>
        </div>
        {!stats?.orders?.length ? (
          <p className="billing-empty">No orders yet.</p>
        ) : (
          <table className="billing-table">
            <thead>
              <tr>
                <th>Order</th>
                <th className="num">Revenue</th>
                <th className="num">Total Cost</th>
                <th className="num">Profit</th>
                <th className="num">Margin</th>
              </tr>
            </thead>
            <tbody>
              {stats.orders.map((o, idx) => (
                <tr key={idx}>
                  <td><strong>{o.label}</strong></td>
                  <td className="num">{fmtINR(o.revenue)}</td>
                  <td className="num">{fmtINR(o.expenses)}</td>
                  <td className="num" style={{ color: o.profit >= 0 ? '#5a7536' : '#a85a2c' }}>{fmtINR(o.profit)}</td>
                  <td className="num">{o.margin}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

const StatCard = ({ label, value, note, tone }) => (
  <div className="billing-stat-card">
    <p className="billing-stat-label">{label}</p>
    <p className="billing-stat-value">{value}</p>
    {note && <p className={`billing-stat-note ${tone || ''}`}>{note}</p>}
  </div>
);

/* ---------------- Orders View ---------------- */
const OrdersView = ({ orders, onEdit, onDelete }) => {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return orders;
    return orders.filter((o) =>
      (o.invoice_number || '').toLowerCase().includes(term)
      || (o.customer || '').toLowerCase().includes(term)
    );
  }, [orders, q]);

  return (
    <div className="billing-section">
      <div className="billing-section-header">
        <h3 className="billing-section-title">Order-wise Expenses</h3>
        <div className="billing-section-toolbar">
          <div style={{ position: 'relative' }}>
            <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
            <input
              className="billing-search"
              style={{ paddingLeft: 30 }}
              placeholder="Search order or customer"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="expense-orders-search"
            />
          </div>
        </div>
      </div>

      {!filtered.length ? (
        <p className="billing-empty">No orders to track yet — create an invoice in <strong>Billing</strong> first.</p>
      ) : (
        <table className="billing-table" data-testid="expense-orders-table">
          <thead>
            <tr>
              <th>Order #</th><th>Customer</th><th>Date</th>
              <th className="num">Revenue</th>
              <th className="num">Material</th><th className="num">Labour</th><th className="num">Embroidery</th><th className="num">Packaging</th>
              <th className="num">Total Cost</th><th className="num">Profit</th><th className="num">Margin</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o, idx) => {
              const cat = { material: 0, labour: 0, embroidery: 0, packaging: 0, other: 0 };
              for (const e of (o.expenses || [])) cat[e.category || 'other'] = (cat[e.category || 'other'] || 0) + Number(e.amount || 0);
              const totalCost = Object.values(cat).reduce((s, v) => s + v, 0);
              const profit = Number(o.revenue || 0) - totalCost;
              const margin = o.revenue > 0 ? Math.round(profit / o.revenue * 100) : 0;
              return (
                <tr key={o.id || `inv-${o.billing_id}-${idx}`} data-testid={`expense-order-row-${idx}`}>
                  <td><strong>{o.invoice_number}</strong>{o._source === 'billing' && <small style={{ color: 'var(--accent-gold)', marginLeft: 6 }}>↗ synced</small>}</td>
                  <td>{o.customer || <span className="muted">—</span>}</td>
                  <td>{fmtDate(o.date)}</td>
                  <td className="num">{fmtINR(o.revenue)}</td>
                  <td className="num">{fmtINR(cat.material)}</td>
                  <td className="num">{fmtINR(cat.labour)}</td>
                  <td className="num">{fmtINR(cat.embroidery)}</td>
                  <td className="num">{fmtINR(cat.packaging)}</td>
                  <td className="num">{fmtINR(totalCost)}</td>
                  <td className="num" style={{ color: profit >= 0 ? '#5a7536' : '#a85a2c' }}>{fmtINR(profit)}</td>
                  <td className="num">{margin}%</td>
                  <td>
                    <div className="billing-row-actions">
                      <button className="billing-row-btn" onClick={() => onEdit(o)} title={o._no_expenses ? 'Add expenses' : 'Edit'}><Pencil size={13} /></button>
                      {o.id && (
                        <button className="billing-row-btn danger" onClick={() => onDelete(o)} title="Remove"><Trash2 size={13} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* ---------------- Order Expense Modal ---------------- */
const OrderExpenseModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    billing_id: initial.billing_id || null,
    invoice_number: initial.invoice_number || '',
    customer: initial.customer || '',
    date: initial.date || todayISO(),
    revenue: initial.revenue ?? 0,
    expenses: initial.expenses?.length ? initial.expenses : [{ desc: '', category: 'material', amount: 0 }],
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const totalCost = form.expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const profit = Number(form.revenue || 0) - totalCost;
  const margin = form.revenue > 0 ? Math.round(profit / form.revenue * 100) : 0;

  const updateRow = (idx, key, val) => {
    setForm((f) => ({
      ...f,
      expenses: f.expenses.map((e, i) => i === idx ? { ...e, [key]: key === 'amount' ? Number(val) : val } : e),
    }));
  };
  const addRow = () => setForm((f) => ({ ...f, expenses: [...f.expenses, { desc: '', category: 'material', amount: 0 }] }));
  const delRow = (idx) => setForm((f) => ({ ...f, expenses: f.expenses.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.invoice_number.trim() && !form.billing_id) { setErr('Invoice number or linked invoice required.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const payload = {
        billing_id: form.billing_id,
        invoice_number: form.invoice_number,
        customer: form.customer,
        date: form.date,
        revenue: Number(form.revenue) || 0,
        expenses: form.expenses.filter((e) => e.desc.trim() || e.amount),
      };
      if (isEdit) await adminApi.updateExpenseOrder(initial.id, payload);
      else await adminApi.createExpenseOrder(payload);
      onSaved(payload, isEdit);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setSubmitting(false); }
  };

  // Revenue is synced from billing if billing_id present
  const syncedRevenue = !!form.billing_id;

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="expense-order-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>
          {isEdit ? 'Edit Order Expenses' : initial._no_expenses ? `Add Expenses for ${initial.invoice_number}` : 'Add Order Expenses'}
        </h2>

        <form onSubmit={submit}>
          <div className="billing-form-grid">
            <div className="billing-field"><label>Invoice / Order #</label>
              <input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} disabled={syncedRevenue} data-testid="expense-input-invoice" />
            </div>
            <div className="billing-field"><label>Customer Name</label>
              <input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} disabled={syncedRevenue} />
            </div>
            <div className="billing-field"><label>Order Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="billing-field"><label>Revenue / Selling Price (₹) {syncedRevenue && <em style={{ fontSize: 10, marginLeft: 6, color: 'var(--accent-gold)' }}>synced from billing</em>}</label>
              <input type="number" min="0" step="100" value={form.revenue} onChange={(e) => setForm({ ...form, revenue: e.target.value })} disabled={syncedRevenue} data-testid="expense-input-revenue" />
            </div>
          </div>

          <div className="billing-items" data-testid="expense-items">
            <div className="billing-items-row head" style={{ gridTemplateColumns: '1fr 160px 160px 36px' }}>
              <div>Description</div><div>Category</div><div>Amount (₹)</div><div></div>
            </div>
            {form.expenses.map((e, idx) => (
              <div key={idx} className="billing-items-row" style={{ gridTemplateColumns: '1fr 160px 160px 36px' }}>
                <input value={e.desc} onChange={(ev) => updateRow(idx, 'desc', ev.target.value)} placeholder="Tissue silk fabric" data-testid={`expense-row-desc-${idx}`} />
                <select value={e.category} onChange={(ev) => updateRow(idx, 'category', ev.target.value)} data-testid={`expense-row-category-${idx}`}>
                  {EXPENSE_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <input className="num" type="number" min="0" step="100" value={e.amount} onChange={(ev) => updateRow(idx, 'amount', ev.target.value)} data-testid={`expense-row-amount-${idx}`} />
                <button type="button" className="row-del" onClick={() => delRow(idx)} title="Remove"><X size={14} /></button>
              </div>
            ))}
          </div>
          <button type="button" className="billing-add-row" onClick={addRow} data-testid="expense-add-row">+ Add expense row</button>

          <div className="billing-totals" style={{ marginTop: 18 }}>
            <div className="billing-totals-row"><span>Total Expenses</span><span>{fmtINR(totalCost)}</span></div>
            <div className="billing-totals-row"><span>Revenue</span><span>{fmtINR(form.revenue)}</span></div>
            <div className="billing-totals-row grand"><span>Net Profit ({margin}%)</span><span>{fmtINR(profit)}</span></div>
          </div>

          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}

          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="expense-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Save Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ---------------- Overhead View ---------------- */
const OverheadView = ({ overheads, onCreate, onEdit, onDelete }) => {
  const total = overheads.reduce((s, o) => s + Number(o.amount || 0), 0);
  const thisMonthIso = new Date().toISOString().slice(0, 7);
  const thisMonthTotal = overheads.filter((o) => (o.date || '').startsWith(thisMonthIso)).reduce((s, o) => s + Number(o.amount || 0), 0);

  return (
    <>
      <div className="billing-stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="billing-stat-card"><p className="billing-stat-label">This Month</p><p className="billing-stat-value">{fmtINR(thisMonthTotal)}</p></div>
        <div className="billing-stat-card"><p className="billing-stat-label">Total Overhead</p><p className="billing-stat-value">{fmtINR(total)}</p></div>
        <div className="billing-stat-card"><p className="billing-stat-label">Entries</p><p className="billing-stat-value">{overheads.length}</p></div>
      </div>

      <div className="billing-section">
        <div className="billing-section-header">
          <h3 className="billing-section-title">Overhead &amp; Running Costs</h3>
          <button className="btn-primary" onClick={onCreate} data-testid="overhead-new-btn">
            <Plus size={14} /> Add Entry
          </button>
        </div>

        {!overheads.length ? (
          <p className="billing-empty">No overhead entries yet.</p>
        ) : (
          <table className="billing-table" data-testid="overhead-table">
            <thead>
              <tr><th>Description</th><th>Category</th><th>Date</th><th className="num">Amount</th><th>Notes</th><th></th></tr>
            </thead>
            <tbody>
              {overheads.map((o) => (
                <tr key={o.id}>
                  <td><strong>{o.desc}</strong></td>
                  <td>{OVH_LABEL[o.category] || o.category}</td>
                  <td>{fmtDate(o.date)}</td>
                  <td className="num">{fmtINR(o.amount)}</td>
                  <td>{o.notes || <span className="muted">—</span>}</td>
                  <td>
                    <div className="billing-row-actions">
                      <button className="billing-row-btn" onClick={() => onEdit(o)} title="Edit"><Pencil size={13} /></button>
                      <button className="billing-row-btn danger" onClick={() => onDelete(o)} title="Delete"><Trash2 size={13} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

/* ---------------- Overhead Modal ---------------- */
const OverheadModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    desc: initial.desc || '',
    category: initial.category || 'other',
    amount: initial.amount ?? 0,
    date: initial.date || todayISO(),
    notes: initial.notes || '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (!form.desc.trim()) { setErr('Description is required.'); return; }
    setErr(''); setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) || 0 };
      if (isEdit) await adminApi.updateOverhead(initial.id, payload);
      else await adminApi.createOverhead(payload);
      onSaved(payload, isEdit);
    } catch (e2) { setErr(formatApiError(e2)); } finally { setSubmitting(false); }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal billing-modal" onClick={(e) => e.stopPropagation()} data-testid="overhead-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>{isEdit ? 'Edit Overhead' : 'Add Overhead Entry'}</h2>
        <form onSubmit={submit}>
          <div className="billing-form-grid">
            <div className="billing-field billing-field-full"><label>Description *</label>
              <input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} required placeholder="Atelier rent — Feb" data-testid="overhead-input-desc" />
            </div>
            <div className="billing-field"><label>Amount (₹) *</label>
              <input type="number" min="0" step="100" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required data-testid="overhead-input-amount" />
            </div>
            <div className="billing-field"><label>Date</label>
              <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="billing-field"><label>Category</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="overhead-input-category">
                {OVERHEAD_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div className="billing-field billing-field-full"><label>Notes</label>
              <textarea rows="2" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}
          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting} data-testid="overhead-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save' : 'Add Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseTab;
